import { useEffect, useState } from "react";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import type { ItemCommsMessage } from "@forgepoint/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { useItemCommsMessagesStore } from "./itemCommsMessagesStore";
import { useItemsStore } from "./itemsStore";
import { CommsProviderBadge } from "./CommsProviderBadge";
import { ItemTabError } from "./ItemTabError";
import { CommsConversationModal } from "./CommsConversationModal";
import { formatCommsTimestamp } from "./commsMessageFormat";

/**
 * Comms messages tab for the item editor: the messages the agent referenced as
 * determining this item's fields, oldest first so the list reads as the
 * decision trail. Each card opens its full conversation in a modal.
 * @param itemId - The item whose referenced messages to show
 */
export const ItemCommsMessagesTab = ({ itemId }: { itemId: string }) => {
  const { messagesByItemId, loadingByItemId, fetchMessages } =
    useItemCommsMessagesStore();
  const messages = messagesByItemId[itemId];
  const loading = loadingByItemId[itemId];
  const [activeMessage, setActiveMessage] = useState<ItemCommsMessage | null>(
    null,
  );
  // Refetches whenever the cached item moves; any writer that bumps the item's
  // updatedAt refreshes the list, agent edits recording references included.
  const itemVersionMs = useItemsStore(
    (state) => state.items.get(itemId)?.updatedAt.getTime() ?? 0,
  );

  useEffect(() => {
    fetchMessages(itemId).catch(() => undefined);
  }, [itemId, itemVersionMs, fetchMessages]);

  if (loading?.isLoading && !messages) {
    return <CommsMessagesSkeleton />;
  }

  if (loading?.error && !messages) {
    return (
      <ItemTabError
        message={loading.error}
        onRetry={() => fetchMessages(itemId).catch(() => undefined)}
      />
    );
  }

  if (!messages || messages.length === 0) {
    return <CommsMessagesEmpty />;
  }

  return (
    <>
      <div className="flex h-full flex-col gap-2 overflow-y-auto p-3">
        {messages.map((message) => (
          <CommsMessageCard
            key={message.commsMessageId}
            message={message}
            onOpen={() => setActiveMessage(message)}
          />
        ))}
      </div>
      <CommsConversationModal
        message={activeMessage}
        onClose={() => setActiveMessage(null)}
      />
    </>
  );
};

const CommsMessageCard = ({
  message,
  onOpen,
}: {
  message: ItemCommsMessage;
  onOpen: () => void;
}) => (
  <button
    type="button"
    onClick={onOpen}
    className="flex w-full flex-col gap-1.5 rounded-lg border bg-card p-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
  >
    <div className="flex items-center gap-2">
      <span className="truncate text-sm font-medium text-foreground">
        {senderOf(message)}
      </span>
      <DirectionTag direction={message.direction} />
      <span className="ml-auto shrink-0 text-xs text-muted-foreground">
        {formatCommsTimestamp(message.createdAt)}
      </span>
    </div>
    <div className="flex items-center gap-2">
      <CommsProviderBadge provider={message.provider} />
      {message.subject ? (
        <span className="truncate text-xs font-medium text-foreground/80">
          {message.subject}
        </span>
      ) : null}
    </div>
    {message.body ? (
      // Collapsed whitespace on purpose: html-derived markdown often opens with
      // blank lines, and with pre-wrap those two lines were all the clamp showed.
      <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
        {message.body}
      </p>
    ) : null}
  </button>
);

const DirectionTag = ({ direction }: { direction: string }) => {
  const inbound = direction !== "outbound";
  const Icon = inbound ? ArrowDownLeft : ArrowUpRight;
  return (
    <span className="inline-flex shrink-0 items-center gap-0.5 text-[11px] text-muted-foreground">
      <Icon className="h-3 w-3" aria-hidden />
      {inbound ? "Received" : "Sent"}
    </span>
  );
};

const CommsMessagesEmpty = () => (
  <div className="flex h-full items-center justify-center p-8">
    <p className="text-sm text-muted-foreground">No correspondence cited.</p>
  </div>
);

const CommsMessagesSkeleton = () => (
  <div className="flex flex-col gap-2 p-3">
    {[0, 1, 2].map((row) => (
      <div key={row} className="flex flex-col gap-2 rounded-lg border p-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="ml-auto h-3 w-16" />
        </div>
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    ))}
  </div>
);

const senderOf = (message: ItemCommsMessage): string =>
  message.senderLabel ?? message.from ?? "Unknown sender";
